import { FC, Fragment } from 'react'
import {
  SectionArticlesTitle,
} from './styles'
import { FirmSectionData } from 'src/dtos/applicationData'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import { TbFileCheck } from 'react-icons/tb'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { IoMdCheckmark } from 'react-icons/io'
import AreasOfPractice from 'src/components/application/mainPagesComponents/Firm/AreasOfPractice'
import { FirmDetailsContactData } from 'src/dtos/portal'

interface FirmSectionProps {
  firmSection?: FirmSectionData;
  isComplete: boolean;
  primaryContactData : FirmDetailsContactData | undefined;
}

const FirmSection: FC<FirmSectionProps> = ({ firmSection, isComplete, primaryContactData }) => {
  const transactions = firmSection?.securitiesLawInfo?.transactions

  return (
    <section>
      <header>
        <h4>Firm</h4>
        {isComplete ? (
          <h6><IoMdCheckmark /> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>FIRM BASICS</SectionArticlesTitle>
      <article>
        <strong>Firm Name</strong>
        <span>{firmSection?.firmName ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Type of Practice</strong>
        <span>{firmSection?.practiceType ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Firm Established</strong>
        <span>{firmSection?.establishmentDate ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Contact Information</strong>
        <span>{primaryContactData?.firstName + ' ' + primaryContactData?.lastName ?? 'N/A'}</span>
        <span>{primaryContactData?.emailAddress ?? 'N/A'}</span>
        <span>{firmSection?.contactInfo?.phoneNumber ?? 'N/A'}</span>
        <span>{firmSection?.contactInfo?.website ?? ''}</span>
        <span>{firmSection?.contactInfo?.faxNumber ?? ''}</span>
      </article>
      <article>
        <strong>Principal Mailing Address</strong>
        <span>{firmSection?.mailAddress?.formattedAddress ?? 'N/A'}</span>
        <span>{(firmSection?.mailAddress?.suiteNumber == null ? ' ' : "STE " + firmSection?.mailAddress?.suiteNumber)}</span>
      </article>
      {firmSection?.isBillingEqualToMail === false ? (
        <article>
          <strong>Billing Address</strong>
          <span>{firmSection?.billingAddress?.formattedAddress}</span>
          <span>{firmSection?.billingAddress?.suiteNumber == null ? ' ' : "STE " + firmSection?.billingAddress?.suiteNumber}</span>
        </article>
      ) : null}
      {!!firmSection?.isSecondaryContactAdded === true ? (
        <article>
          <strong>Secondary Contact</strong>
          <span>{[firmSection?.secondaryContact?.firstName, firmSection?.secondaryContact?.lastName].join(' ') ?? 'N/A'}</span>
          <span>{firmSection?.secondaryContact?.emailAddress ?? ''}</span>
          <span>{firmSection?.secondaryContact?.phoneNumber ?? ''}</span>
          {firmSection?.secondaryContact?.isFirmAdministrator === true ? (
            <span><IoIosCheckmarkCircle /> Firm Administrator</span>
          ) : null}
        </article>
      ) : null}
      <article>
        <strong>Number of Offices</strong>
        <span>{firmSection?.numberOfOffices ?? 'N/A'}</span>
      </article>

      <SectionArticlesTitle>AREAS OF PRACTICE</SectionArticlesTitle>
      <article>
        {firmSection?.practiceAreas ?
          <AreasOfPractice practiceAreas={firmSection?.practiceAreas} />
          : null}
      </article>

      {!!firmSection?.practiceAreas?.find(a => a.area === 'Real Estate') ? (
        <>
          <SectionArticlesTitle>REAL ESTATE</SectionArticlesTitle>
          <article>
            <strong>Is any firm member a licensed escrow officer for a title company?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberWithEscrowLicense)}</span>
          </article>
          <article>
            <strong>Does Applicant or any firm member have any agreements with title companies?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies)}</span>
            {firmSection?.realEstateInfo?.hasAgreementWithTitleCompanies === true ? firmSection?.realEstateInfo?.agreementWithTitleCompaniesFile?.map(file =>
              <span key={file?.fileName} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file?.fileName ?? 'N/A'}</span>
            ) : null}
          </article>
          <article>
            <strong>Is any firm member a fee attorney for a title company?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberFeeAttorney)}</span>
          </article>
          <article>
            <strong>Is any firm member an outside closing attorney, approved attorney or P-22 attorney?</strong>
            <span>{handleYesNoAnswers(firmSection?.realEstateInfo?.hasFirmMemberOutsideClosingAttorney)}</span>
          </article>
          <article>
            <strong>How many non-attorney licensed escrow officers are currently employed by your firm?</strong>
            <span>{firmSection?.realEstateInfo?.nonAttorneysWithEscrowLicenseQuantity ?? 'N/A'}</span>
          </article>
        </>
      ) : null}

      {!!firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt' || a.area === 'Securities - Other Types Of Securities Practice' ) ? (
        <>
          <SectionArticlesTitle>SECURITIES LAW</SectionArticlesTitle>
          {firmSection?.securitiesLawInfo?.hasFiles === true ? (
            <>
              <article>
                <strong>
                  Uploaded files of securities transactions
                </strong>
                {firmSection?.securitiesLawInfo?.securitiesLawFiles?.map(file => (
                  <span key={file.fileName} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file.fileName}</span>
                ))}
              </article>
            </>
          ) : (
            <>
              {transactions && transactions.length > 0 ? transactions?.map((transaction, index) =>
                <Fragment key={index}>
                  <article><span><h6>Transaction {index + 1}</h6></span></article>
                  <article>
                    <strong>
                      Date
                    </strong>
                    <span>{transaction?.date ?? 'N/A'}</span>
                  </article>
                  <article>
                    <strong>
                      Client name
                    </strong>
                    <span>{transaction?.clientName ?? 'N/A'}</span>
                  </article>
                  <article>
                    <strong>
                      Size of offering
                    </strong>
                    <span>{transaction?.offeringSize ?? 'N/A'}</span>
                  </article>
                  <article>
                    <strong>
                      Role in transaction
                    </strong>
                    <span>{transaction?.transactionRole ?? 'N/A'}</span>
                  </article>
                </Fragment>
              ) : null}
            </>
          )}
        </>
      ) : null}

      <SectionArticlesTitle>RELATIONSHIPS</SectionArticlesTitle>
      <article>
        <strong>Has the Firm ever sued a client for fees?</strong>
        <span>{handleYesNoAnswers(firmSection?.relationships?.hasSuedClientForFees)}</span>
      </article>
      {firmSection?.relationships?.hasSuedClientForFees === true ? (
        <>
          <SectionArticlesTitle>LOSS PREVENTION</SectionArticlesTitle>
          <article>
            <strong>Does Applicant share office spaces or expenses with another attorney or law firm?</strong>
            <span>{handleYesNoAnswers(firmSection?.lossPrevention?.shareOfficeSpaceOrExpenses)}</span>
            {firmSection?.lossPrevention?.shareOfficeSpaceOrExpenses === true ?
              firmSection?.lossPrevention?.sharedSpaceExpensesAttorneys?.map((shared, index) => (
                <>
                  <br />
                  <span><h6>Shared Expense Attorney {index + 1}</h6></span>
                  <span><h6>Attorney or Law Firm name:</h6>{shared?.fullName ?? 'N/A'}</span>
                  <span><h6>Other Attorney Bar Number:</h6>{shared?.barCardNumber ?? 'N/A'}</span>
                  <span><h6>Date sharing began:</h6>{shared?.dateSharingBegan ?? 'N/A'}</span>
                </>
              )) :
              null
            }
          </article>
          <article>
            <strong>If Applicant is a sole practitioner, who is responsible for Applicant's practice in the event of a prolonged absence?</strong>
            <span>{firmSection?.lossPrevention?.prolongedAbsenceResponsible ?? 'N/A'}</span>
          </article>
          <article>
            <strong>How does Applicant handle the scheduling of work, deadlines and appointments?</strong>
            {firmSection?.lossPrevention?.workScheduling?.map(work => (
              <span key={work}>{work ?? 'N/A'}</span>
            ))}
            {!!firmSection?.lossPrevention?.workSchedulingOther ? <span>{firmSection?.lossPrevention?.workSchedulingOther}</span> : null}
            {(!!firmSection?.lossPrevention?.workSchedulingFiles &&
              firmSection?.lossPrevention?.workSchedulingFiles.length > 0) ?
              firmSection?.lossPrevention?.workSchedulingFiles.map(file => (
                <span key={file.id} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file.fileName}</span>
              )) :
              null
            }
          </article>
          <article>
            <strong>Do all firm members use engagement letters that specify scope of services, terms and billing rates?</strong>
            <span>{handleYesNoAnswers(firmSection?.lossPrevention?.engagementLetters)}</span>
          </article>
          <article>
            <strong>Do all firm members use non-engagement letter when declining a representation?</strong>
            <span>{handleYesNoAnswers(firmSection?.lossPrevention?.nonEngagementLettersWhenDeclining)}</span>
          </article>
          <article>
            <strong>Do all firm members use disengagement letter when ceasing representation?</strong>
            <span>{handleYesNoAnswers(firmSection?.lossPrevention?.disengagementLettersWhenCeasing)}</span>
          </article>
          <article>
            <strong>How does Applicant avoid conflicts of interest among and with clients?</strong>
            {firmSection?.lossPrevention?.avoidConflictOfInterests?.map(coi => (
              <span key={coi}>{coi ?? 'N/A'}</span>
            ))}
            {!!firmSection?.lossPrevention?.avoidConflictOfInterestsOther ? <span>{firmSection?.lossPrevention?.avoidConflictOfInterestsOther}</span> : null}
            {(!!firmSection?.lossPrevention?.avoidConflictOfInterestsFiles &&
              firmSection?.lossPrevention?.avoidConflictOfInterestsFiles.length > 0) ?
              firmSection?.lossPrevention?.avoidConflictOfInterestsFiles.map(file => (
                <span key={file.id} style={{ flexDirection: 'row', alignItems: 'center' }}><TbFileCheck /> {file.fileName}</span>
              )) :
              null
            }
          </article>
          <article>
            <strong>If a potential conflict of interest exists, do all firm members obtain written conflicts waivers that clearly:</strong>
            <span><h6>Advise client(s) of the nature of potential conflict? </h6></span>
            <span>{handleYesNoAnswers(firmSection?.lossPrevention?.conflictWaiversPotentialConflict)}</span>
            <span><h6>Advise client(s) how the potential conflict could affect representation? </h6></span>
            <span>{handleYesNoAnswers(firmSection?.lossPrevention?.conflictWaiversRepresentationAffected)}</span>
            <span><h6>Advise client(s) to consult another attorney about the potential conflict? </h6></span>
            <span>{handleYesNoAnswers(firmSection?.lossPrevention?.conflictWaiversAnotherAttorneyConsult)}</span>
          </article>
          <article>
            <strong>How many times in the last two years has Applicant sued a client for fees?</strong>
            <span>{firmSection?.lossPrevention?.timesSuedClientsForFees ?? 'N/A'}</span>
          </article>
          <article>
            <strong>How many clients that were sued, if any, filed a counterclaim for legal malpractice or breach of fiduciary duty?</strong>
            <span>{firmSection?.lossPrevention?.howManyClientsSuedFiledCounterClaim ?? 'N/A'}</span>
          </article>
        </>
      ) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{firmSection?.review?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!firmSection?.review?.files && firmSection?.review?.files.length > 0 ? 
          firmSection?.review?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default FirmSection